import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";
import Layout from "@/components/Layout";
import { canAccess, ROUTE_ROLES, landingPath } from "@/lib/nav";

import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ForcePasswordChange from "@/pages/ForcePasswordChange";
import Dashboard from "@/pages/Dashboard";
import Employees from "@/pages/Employees";
import EmployeeDetail from "@/pages/EmployeeDetail";
import SalaryRates from "@/pages/SalaryRates";
import Projects from "@/pages/Projects";
import AllowanceTypes from "@/pages/AllowanceTypes";
import Attendance from "@/pages/Attendance";
import CashAdvances from "@/pages/CashAdvances";
import KasbonRequests from "@/pages/KasbonRequests";
import Allowances from "@/pages/Allowances";
import Deductions from "@/pages/Deductions";
import PayrollProcess from "@/pages/PayrollProcess";
import PayrollHistory from "@/pages/PayrollHistory";
import Payments from "@/pages/Payments";
import Payslips from "@/pages/Payslips";
import ReportPayroll from "@/pages/ReportPayroll";
import ReportProject from "@/pages/ReportProject";
import ReportAttendance from "@/pages/ReportAttendance";
import ReportAttendanceRealtime from "@/pages/ReportAttendanceRealtime";
import ReportAttendanceRange from "@/pages/ReportAttendanceRange";
import NotaBelanja from "@/pages/NotaBelanja";
import NotaRekap from "@/pages/NotaRekap";
import Vehicles from "@/pages/Vehicles";
import VehicleHistory from "@/pages/VehicleHistory";
import ReportMandorCheck from "@/pages/ReportMandorCheck";
import ReportOvertime from "@/pages/ReportOvertime";
import ReportOvertimeHigh from "@/pages/ReportOvertimeHigh";
import ReportKasbon from "@/pages/ReportKasbon";
import Users from "@/pages/Users";
import AuditLog from "@/pages/AuditLog";
import Settings from "@/pages/Settings";

function Protected({ children, roles, path }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-blue-800" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ForcePasswordChange />;
  if (roles && !canAccess(roles, user.role, user.extra_menus, path))
    return <Navigate to={landingPath(user.role)} replace />;
  return <Layout>{children}</Layout>;
}

const P = (el, path) => <Protected roles={path ? ROUTE_ROLES[path] : undefined} path={path}>{el}</Protected>;

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          <Route path="/" element={P(<Dashboard />, "/")} />
          <Route path="/employees" element={P(<Employees />, "/employees")} />
          <Route path="/employees/:id" element={P(<EmployeeDetail />, "/employees")} />
          <Route path="/salary-rates" element={P(<SalaryRates />, "/salary-rates")} />
          <Route path="/projects" element={P(<Projects />, "/projects")} />
          <Route path="/allowance-types" element={P(<AllowanceTypes />, "/allowance-types")} />
          <Route path="/attendance" element={P(<Attendance />, "/attendance")} />
          <Route path="/cash-advances" element={P(<CashAdvances />, "/cash-advances")} />
          <Route path="/kasbon-requests" element={P(<KasbonRequests />, "/kasbon-requests")} />
          <Route path="/allowances" element={P(<Allowances />, "/allowances")} />
          <Route path="/deductions" element={P(<Deductions />, "/deductions")} />
          <Route path="/payroll" element={P(<PayrollProcess />, "/payroll")} />
          <Route path="/payroll-history" element={P(<PayrollHistory />, "/payroll-history")} />
          <Route path="/payments" element={P(<Payments />, "/payments")} />
          <Route path="/payslips" element={P(<Payslips />, "/payslips")} />
          <Route path="/nota" element={P(<NotaBelanja />, "/nota")} />
          <Route path="/nota/persetujuan" element={P(<NotaBelanja approval />, "/nota/persetujuan")} />
          <Route path="/nota/rekap" element={P(<NotaRekap />, "/nota/rekap")} />
          <Route path="/purchases" element={<Navigate to="/nota" replace />} />
          <Route path="/vehicles" element={P(<Vehicles />, "/vehicles")} />
          <Route path="/vehicles/:id" element={P(<VehicleHistory />, "/vehicles")} />
          <Route path="/petty-cash" element={<Navigate to="/nota" replace />} />
          <Route path="/reports/petty-cash" element={<Navigate to="/nota" replace />} />
          <Route path="/reports/attendance-realtime" element={P(<ReportAttendanceRealtime />, "/reports/attendance-realtime")} />
          <Route path="/reports/attendance-range" element={P(<ReportAttendanceRange />, "/reports/attendance-range")} />
          <Route path="/reports/mandor-check" element={P(<ReportMandorCheck />, "/reports/mandor-check")} />
          <Route path="/reports/payroll" element={P(<ReportPayroll />, "/reports/payroll")} />
          <Route path="/reports/project" element={P(<ReportProject />, "/reports/project")} />
          <Route path="/reports/attendance" element={P(<ReportAttendance />, "/reports/attendance")} />
          <Route path="/reports/overtime" element={P(<ReportOvertime />, "/reports/overtime")} />
          <Route path="/reports/overtime-high" element={P(<ReportOvertimeHigh />, "/reports/overtime-high")} />
          <Route path="/reports/kasbon" element={P(<ReportKasbon />, "/reports/kasbon")} />
          <Route path="/users" element={P(<Users />, "/users")} />
          <Route path="/audit-logs" element={P(<AuditLog />, "/audit-logs")} />
          <Route path="/settings" element={P(<Settings />, "/settings")} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
